/**
 * Yard sale signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_everyday_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const yardSaleSignsPack: ToilePack = {
    slug: 'yard-sale-signs',
    category: 'shopfront',
    name: 'Yard sale signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'arrow', label: 'Arrow sign', subject: 'a sign reading "Yard Sale" in huge letters with a big arrow pointing right'},
        {key: 'time', label: 'Day and time', subject: 'a sign reading "Garage Sale" with a smaller line "Sat 8 AM" and a few drawn price tags'},
        {key: 'clearance', label: 'Everything goes', subject: 'a sign reading "Everything Must Go" in bold stacked letters with starbursts in the corners'},
    ],
    styles: [
        {key: 'neon', label: 'Neon poster board', style: 'fluorescent poster-board sign, thick black marker lettering, hard edges and a bold border', palette: 'a palette of neon yellow and black', framing: 'Centered, the lettering filling the board with a thin margin all round.'},
        {key: 'redwhite', label: 'Red and white', style: 'bold two-color sign, huge block lettering, hard edges and a heavy outline', palette: 'a two-color palette of red and white', framing: 'Centered, the lettering filling the board with a thin margin all round.'},
        {key: 'handmade', label: 'Hand-lettered', style: 'hand-lettered cardboard sign, brush-marker letters with slight wobble and a cardboard grain texture', palette: 'a palette of black marker and one bright orange on kraft brown', framing: 'Centered, the lettering filling the board with a thin margin all round.'},
    ],
    directions: ['moving sale', 'blue', 'balloons'],
    directionHint: 'try: moving sale',
    seo: {
        title: 'Yard Sale Signs: Arrow, Day and Time, Clearance',
        metaDescription: 'Nine yard sale signs a driver reads at a glance: an arrow sign, a day and time sign and a clearance sign, in three board styles.',
        h1: 'Yard sale signs: arrow sign, day and time, everything must go',
        tagline: 'Three one-glance roadside signs, each on neon board, in red and white or hand-lettered style.',
        intro: 'Households holding a sale need a sign that drivers read in one glance. These nine landscape signs show a Yard Sale sign with a big arrow pointing right, a Garage Sale sign with a Sat 8 AM line and drawn tags, and a bold Everything Must Go sign with starbursts in the corners. Each comes on neon poster board, in red and white, or hand-lettered on kraft card. Type a request, such as moving sale, to shape it.',
        useCases: [
            'a sign at the corner near the house',
            'a sign on a garage door',
            'a board for a clearance sale',
            'a sign for a moving sale',
        ],
        faq: [
            {q: 'Does the sign show a date?', a: 'The day and time sign shows a sample line, Sat 8 AM, which you can change. The other two carry only their wording.'},
            {q: 'What are the three styles?', a: 'Neon poster board uses thick black marker lettering on neon yellow, red and white is a bold two-colour sign with a heavy outline, and hand-lettered uses brush-marker letters on kraft brown.'},
            {q: 'Can I get a different colour?', a: 'Yes, as a request, such as blue. The nine signs show the three styles above.'},
        ],
    },
}
